import { formatDistanceToNow } from 'date-fns';
import parse, {
  type DOMNode,
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import { Clock } from 'lucide-react';
import { Link } from 'react-router';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/shadcn/tooltip';
import { getDateFnsLocale } from '@/lib/i18n/dateFns';

import transformDeeplink from '../deeplink';
import { formatDateTimeLong } from '../utils/date';

const parseLink = ({ attribs, children }: Element) => (
  <Link
    to={transformDeeplink(attribs?.href ?? '')}
    className="text-primary-500 inline hover:underline"
  >
    {domToReact(children as DOMNode[], options)}
  </Link>
);

const parseTime = ({ attribs }: Element) => {
  const datetime = attribs?.datetime;
  if (!datetime) return undefined;

  const date = new Date(datetime);
  const relativeTime = formatDistanceToNow(date, {
    addSuffix: true,
    locale: getDateFnsLocale(),
  });

  return (
    <Tooltip>
      <TooltipTrigger className="text-foreground/50 flex items-center gap-1">
        <Clock className="size-3" strokeWidth={2.5} /> {relativeTime}
      </TooltipTrigger>
      <TooltipContent>{formatDateTimeLong(date)}</TooltipContent>
    </Tooltip>
  );
};

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (domNode instanceof Element && domNode.name === 'a') {
      return parseLink(domNode);
    }

    if (domNode instanceof Element && domNode.name === 'time') {
      return parseTime(domNode);
    }
  },
};

export const parseHtml = (html: string) => parse(html, options);
